import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TeamFormData } from '../../_models/teamFormData';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import { TeamsService } from '../../_services/teams.service';
import { TeamReqDto } from '../../_dtos/teamReqDto';
import { AuthService } from '../../_services/auth.service';
import { SharedService } from '../../_services/shared.service';
import {
  debounceTime,
  distinctUntilChanged,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { ValidatorService } from '../../_services/validator.service';
import { CommonModule } from '@angular/common';
import { BaseMetadata } from '../../_models/baseMetadata';
import { DepartmentResDto } from '../../_dtos/departmentResDto';
import { CategoryResDto } from '../../_dtos/categoryResDto';
import { DepartmentsService } from '../../_services/departments.service';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { TranslatedToastService } from '../../_services/translated-toast.service';
import { UserResDto } from '../../_dtos/userResDto';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { GraphService } from '../../_services/graph.service';
import { CategoriesService } from '../../_services/categories.service';

@Component({
  selector: 'app-order-form-order-form-standard-teams',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    MatButton,
    MatError,
    CommonModule,
    MatTooltip,
    TranslateModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
  ],
  templateUrl: './order-form-standard-teams.component.html',
  standalone: true,
  styleUrl: './order-form-standard-teams.component.css',
})
export class OrderFormStandardTeamsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  public department!: DepartmentResDto;
  public departments!: DepartmentResDto[];
  public categories!: CategoryResDto[];
  public standardTeamForm!: FormGroup<TeamFormData>;
  public category!: string;
  public departmentShortName!: string;
  secondaryOwnerControl = new FormControl<UserResDto | string>('');
  filteredPeople?: Observable<UserResDto[]>;

  constructor(
    private teamService: TeamsService,
    private authService: AuthService,
    private toastr: TranslatedToastService,
    private sharedService: SharedService,
    private validatorService: ValidatorService,
    private departmentsService: DepartmentsService,
    private graphService: GraphService,
    private categoriesService: CategoriesService,
  ) {}

  ngOnInit() {
    this.getDataFromSharedService();
    this.initializeForm();
    this.setupFormListeners();
    this.prefillForm();
    this.setupSecondaryOwnerAutocomplete();
  }

  displayUserFn(user: UserResDto | null): string {
    return user ? `${user.displayName}` : '';
  }

  async onSubmit() {
    if (this.standardTeamForm.valid) {
      const formValues = this.standardTeamForm.getRawValue();
      const metadata: BaseMetadata = {
        visibility: formValues.visibility,
        department: this.departmentShortName,
      };

      const username = this.authService.getUsername();
      const teamRequestDto: TeamReqDto = {
        name: formValues.name,
        mailNickname: formValues.mailNickname,
        category: formValues.category,
        primaryOwner: username,
        secondaryOwner: formValues.secondaryOwner?.userPrincipalName!,
        createdBy: username,
        metadata: metadata,
      };

      this.sharedService.closeModal();
      await this.toastr.show(
        'success',
        'TOASTS.TEAMS.SUBMIT_MESSAGE',
        'TOASTS.TEAMS.SUBMIT_TITLE',
      );

      this.teamService.createTeam(teamRequestDto).subscribe({
        next: async () => {
          await this.toastr.show(
            'success',
            'TOASTS.TEAMS.CREATION_SUCCESS',
            undefined,
            { teamName: teamRequestDto.name },
          );
        },
      });
    }
  }

  get name() {
    return this.standardTeamForm.get('name');
  }

  get errorMessage() {
    if (this.name?.hasError('required')) return 'Name is required';
    else if (this.name?.hasError('hasForbiddenChars'))
      return 'Contains forbidden Character';
    else if (this.name?.hasError('exceedsMaxLength'))
      return 'The provided name is too long';
    else if (this.name?.hasError('mailNicknameUnavailable'))
      return 'This name is already taken';
    else return '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm() {
    this.standardTeamForm = new FormGroup<TeamFormData>({
      name: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
        asyncValidators: [
          this.validatorService.validateMailNickname(this.department.shortName),
        ],
        updateOn: 'blur',
      }),
      mailNickname: new FormControl(
        { value: '', disabled: true },
        { nonNullable: true },
      ),
      category: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      primaryOwner: new FormControl(
        { value: this.authService.getDisplayName(), disabled: true },
        { nonNullable: true },
      ),
      secondaryOwner: new FormControl<UserResDto | null>(null, {
        nonNullable: false,
        validators: [this.validatorService.userObjectValidator()],
      }),
      visibility: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      department: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });

    this.secondaryOwnerControl = this.standardTeamForm.get(
      'secondaryOwner',
    ) as FormControl<UserResDto | null>;
  }

  private getDataFromSharedService() {
    this.departments = this.departmentsService.getDepartments();
    this.categories = this.categoriesService.getSpCategories();
    this.department = this.departmentsService.getDepartment();
    const userType = this.graphService.getUserType();
    this.category = userType ? userType.toLowerCase() : 'NO CATEGORY FOUND!';
  }

  private prefillForm() {
    this.standardTeamForm.patchValue({
      department: this.department.name,
      category: this.category,
    });
  }

  onSecondaryOwnerSelected(event: MatAutocompleteSelectedEvent) {
    const selectedUser = event.option.value as UserResDto;
    this.secondaryOwnerControl.setValue(selectedUser);
  }

  private setupFormListeners() {
    this.standardTeamForm.get('name')?.valueChanges.subscribe((value) => {
      const revisedValue = this.validatorService.reviseValue(value);
      const fullMailNickname = this.standardTeamForm.get('mailNickname')!;

      if (!fullMailNickname.value) {
        const newValue = `${this.departmentShortName}-${revisedValue}`;
        this.standardTeamForm
          .get('mailNickname')
          ?.setValue(newValue, { emitEvent: false });
      } else {
        const parts = fullMailNickname?.value.split('-');
        if (parts.length > 0) {
          const department = parts[0];
          const newValue = `${department}-${revisedValue}`;
          this.standardTeamForm
            .get('mailNickname')
            ?.setValue(newValue, { emitEvent: false });
        }
      }
    });

    this.standardTeamForm
      .get('department')!
      .valueChanges.subscribe((departmentName) => {
        this.departmentsService
          .getDepartmentObservable(departmentName)
          .subscribe({
            next: (data) => {
              this.departmentShortName = data.shortName;

              const fullMailNickname =
                this.standardTeamForm.get('mailNickname')!;
              const parts = fullMailNickname.value.split('-');
              if (parts.length > 1) {
                const mailNickname = parts.slice(1).join('-');
                const revisedValue = `${this.departmentShortName}-${mailNickname}`;
                this.standardTeamForm
                  .get('mailNickname')
                  ?.setValue(revisedValue, { emitEvent: false });
              }
            },
          });
      });
  }

  private setupSecondaryOwnerAutocomplete() {
    this.filteredPeople = this.secondaryOwnerControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((searchValue) => {
        if (typeof searchValue !== 'string' || !searchValue.trim())
          return of([]);

        const searchTerm = searchValue.trim();
        return this.graphService.searchPeople(searchTerm);
      }),
    );
  }
}
